/**
 * Projecting recurring rules (/recurring-incomes, /recurring-expenses) onto
 * calendar dates.
 *
 * What comes out of here is a forecast, not money. The backend turns an
 * occurrence into a real income/expense only when the user accepts its
 * recurring notification (recurring-notifications/{id}/accept), so the
 * calendar draws these dates without ever writing a transaction - next month's
 * salary must not land in today's balance.
 *
 * Everything is local calendar dates: date-only strings parsed through
 * `new Date(y, m, d)` rather than Date.parse, which reads "2026-09-23" as UTC
 * midnight and shows the day before west of Greenwich.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** How far a rule steps between two occurrences. */
const STEPS = {
	daily: { unit: 'day', size: 1 },
	weekly: { unit: 'day', size: 7 },
	biweekly: { unit: 'day', size: 14 },
	'bi-weekly': { unit: 'day', size: 14 },
	fortnightly: { unit: 'day', size: 14 },
	monthly: { unit: 'month', size: 1 },
	quarterly: { unit: 'month', size: 3 },
	yearly: { unit: 'month', size: 12 },
	annually: { unit: 'month', size: 12 },
};

/** The rest of the app falls back to monthly for an unknown period; so do we. */
const DEFAULT_STEP = STEPS.monthly;

/** A rule with a broken start date could otherwise spin forever. */
const MAX_OCCURRENCES_PER_RANGE = 400;

export function stepFor(recurringPeriod) {
	if (!recurringPeriod) return DEFAULT_STEP;
	return STEPS[String(recurringPeriod).toLowerCase().trim()] || DEFAULT_STEP;
}

/** Local YYYY-MM-DD, the format every transaction endpoint here speaks. */
export function toDateKey(date) {
	if (!date || Number.isNaN(date.getTime())) return '';
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/** Parse "2026-09-23" or "2026-09-23T10:00:00Z" as that calendar day, locally. */
export function parseDateKey(value) {
	if (!value) return null;
	if (value instanceof Date) {
		return Number.isNaN(value.getTime())
			? null
			: new Date(value.getFullYear(), value.getMonth(), value.getDate());
	}
	const datePart = String(value).split('T')[0].split(' ')[0];
	if (!DATE_ONLY.test(datePart)) return null;
	const [year, month, day] = datePart.split('-').map(Number);
	const parsed = new Date(year, month - 1, day);
	// Rejects the likes of 2026-02-31, which Date would roll into March.
	if (
		parsed.getFullYear() !== year ||
		parsed.getMonth() !== month - 1 ||
		parsed.getDate() !== day
	) {
		return null;
	}
	return parsed;
}

export function addDays(date, days) {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * Add months keeping the anchor day, clamped to the length of the target
 * month: the 31st becomes Feb 28 (29 in a leap year) and then goes back to the
 * 31st in March, because every step is measured from the original start date
 * rather than from the clamped one.
 */
export function addMonthsClamped(date, months) {
	const firstOfTarget = new Date(
		date.getFullYear(),
		date.getMonth() + months,
		1,
	);
	const lastDayOfTarget = new Date(
		firstOfTarget.getFullYear(),
		firstOfTarget.getMonth() + 1,
		0,
	).getDate();
	return new Date(
		firstOfTarget.getFullYear(),
		firstOfTarget.getMonth(),
		Math.min(date.getDate(), lastDayOfTarget),
	);
}

/** Whole days between two local midnights, DST-proof (a day can be 23h or 25h). */
function daysBetween(from, to) {
	return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

/**
 * Every date a rule falls on inside [rangeStart, rangeEnd], both ends
 * inclusive, as YYYY-MM-DD strings in ascending order.
 *
 * Inactive rules and rules whose end_date has passed produce nothing, and
 * nothing is ever produced before start_date - a rule that starts in November
 * leaves September empty rather than back-filling it.
 */
export function occurrencesInRange(rule, rangeStart, rangeEnd) {
	if (!rule || rule.is_active === false) return [];

	const start = parseDateKey(rule.start_date || rule.date);
	const windowStart = parseDateKey(rangeStart);
	const windowEnd = parseDateKey(rangeEnd);
	if (!start || !windowStart || !windowEnd) return [];

	const ruleEnd = parseDateKey(rule.end_date);
	const from = start > windowStart ? start : windowStart;
	const to = ruleEnd && ruleEnd < windowEnd ? ruleEnd : windowEnd;
	if (from > to) return [];

	const step = stepFor(rule.recurring_period || rule.frequency);
	const dates = [];

	if (step.unit === 'day') {
		const elapsed = daysBetween(start, from);
		// Back up one step: `from` may itself sit on an occurrence.
		let index = Math.max(0, Math.ceil(elapsed / step.size) - 1);
		for (let guard = 0; guard < MAX_OCCURRENCES_PER_RANGE; guard += 1) {
			const date = addDays(start, index * step.size);
			if (date > to) break;
			if (date >= from) dates.push(toDateKey(date));
			index += 1;
		}
		return dates;
	}

	const monthsElapsed =
		(from.getFullYear() - start.getFullYear()) * 12 +
		(from.getMonth() - start.getMonth());
	let index = Math.max(0, Math.ceil(monthsElapsed / step.size) - 1);
	for (let guard = 0; guard < MAX_OCCURRENCES_PER_RANGE; guard += 1) {
		const date = addMonthsClamped(start, index * step.size);
		if (date > to) break;
		if (date >= from) dates.push(toDateKey(date));
		index += 1;
	}
	return dates;
}

/**
 * Spread a list of `{ rule, type }` entries over a date range and group them by
 * day, so a calendar cell is one lookup. Several rules - and several
 * occurrences of the same category - can share a date; they keep the order
 * they were given, income before expense, which is how the chips read.
 */
export function groupOccurrencesByDate(entries, rangeStart, rangeEnd) {
	const byDate = new Map();

	(entries || []).forEach(entry => {
		if (!entry?.rule) return;
		occurrencesInRange(entry.rule, rangeStart, rangeEnd).forEach(dateKey => {
			const bucket = byDate.get(dateKey);
			const occurrence = {
				...entry,
				dateKey,
				// Unique per rule per date: React keys, and the edit modal's identity.
				key: `${entry.type}-${entry.rule.id}-${dateKey}`,
			};
			if (bucket) {
				bucket.push(occurrence);
			} else {
				byDate.set(dateKey, [occurrence]);
			}
		});
	});

	return byDate;
}

/**
 * The 6-week grid a month is drawn on: whole weeks, so the first row starts on
 * the week's first day and trailing days of the neighbouring months fill the
 * edges. `weekStartsOn` is 0 for Sunday, 1 for Monday.
 */
export function buildMonthGrid(year, month, weekStartsOn = 1) {
	const firstOfMonth = new Date(year, month, 1);
	const lead = (firstOfMonth.getDay() - weekStartsOn + 7) % 7;
	const gridStart = addDays(firstOfMonth, -lead);

	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const weeks = Math.ceil((lead + daysInMonth) / 7);

	return Array.from({ length: weeks * 7 }, (_, index) => {
		const date = addDays(gridStart, index);
		return {
			date,
			dateKey: toDateKey(date),
			inMonth: date.getMonth() === month && date.getFullYear() === year,
		};
	});
}
